// @ts-nocheck
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Active client switcher (ADR-024), extracted from XDialSidebar so the old
 * sidebar and the new rail (W1) show the same control. Selection is
 * mandatory: an unknown or empty selection falls back to the first client.
 * Hidden when the tenant has no client yet.
 */
export function CustomerSwitcher({ customers, selectedCustomerId, onCustomerChange, open }) {
  const { t } = useTranslation(['dialer']);
  if (!customers || customers.length === 0) return null;
  const clientActif = (selectedCustomerId && customers.find((c) => c.id === selectedCustomerId)) || customers[0];
  const hasMultiple = customers.length > 1;
  // ADR-024 — selection is mandatory. If selectedCustomerId is null or
  // points at an unknown customer, fall back to the first one. This
  // matches the "Tous les clients" option being removed: there is
  // always exactly one Customer in scope from the user's POV.
  const current = clientActif || customers[0];
  const currentLabel = String(current.company_name ?? '');

  const Avatar = ({ label, size = 26 }: { label: string | null | undefined; size?: number }) => {
    const text = String(label ?? '');
    const ini = text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('')
      .slice(0, 2) || '?';
    // DESIGN.md: no decorative gradient, AA text (DES-20); 11px minimum.
    return (
      <div
        aria-hidden
        style={{
          width: size,
          height: size,
          borderRadius: size / 4,
          background: 'var(--vf-accent-bg)',
          border: '1px solid var(--vf-accent-bd)',
          color: 'var(--vf-accent-text)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {ini}
      </div>
    );
  };

  const trigger = (
    <button
      type="button"
      className="w-full flex items-center group transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vf-accent)]"
      style={{
        padding: open ? '8px 10px' : '6px',
        borderRadius: 10,
        background: 'var(--vf-surface-2)',
        border: '1px solid var(--vf-border)',
        gap: 10,
        cursor: hasMultiple ? 'pointer' : 'default',
        marginBottom: 6,
        justifyContent: open ? 'flex-start' : 'center',
      }}
      onMouseEnter={(e) => {
        if (hasMultiple) {
          e.currentTarget.style.background = 'var(--vf-surface-3, var(--vf-surface-2))';
          e.currentTarget.style.borderColor = 'var(--vf-accent)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--vf-surface-2)';
        e.currentTarget.style.borderColor = 'var(--vf-border)';
      }}
      title={open ? (hasMultiple ? t('dialer:xdialSidebar.switchCustomer') : currentLabel) : currentLabel}
    >
      <Avatar label={currentLabel} size={open ? 26 : 28} />
      {open && (
        <>
          <div className="flex-1 min-w-0 text-left">
            <div
              className="truncate"
              style={{ fontSize: 12, color: 'var(--vf-text-1)', fontWeight: 600, lineHeight: 1.2 }}
            >
              {currentLabel}
            </div>
            <div
              className="truncate"
              style={{ fontSize: 11, color: 'var(--vf-text-3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 1, fontWeight: 500 }}
            >
              {current ? t('dialer:xdialSidebar.activeCustomer') : t('dialer:xdialSidebar.customerCount', { count: customers.length })}
            </div>
          </div>
          {(hasMultiple || current) && (
            <ChevronDown
              size={13}
              style={{ color: 'var(--vf-text-3)', flexShrink: 0 }}
              className="group-hover:translate-y-[1px] transition-transform"
            />
          )}
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-64 p-1"
      >
        <div
          className="px-2 py-1.5"
          style={{ borderBottom: '1px solid var(--vf-border)' }}
        >
          <p
            className="font-semibold uppercase"
            style={{ fontSize: 11, color: 'var(--vf-text-3)', letterSpacing: '0.06em' }}
          >
            {t('dialer:xdialSidebar.customerMenuTitle')}
          </p>
        </div>
        <div className="py-1">
          {customers.map((c) => {
            // current is always defined (fallback to customers[0] above);
            // use it instead of selectedCustomerId so the highlight
            // matches what the user actually sees as "active".
            const isCurrent = c.id === current.id;
            const label = String(c.company_name ?? '');
            return (
              <DropdownMenuItem
                key={c.id}
                onClick={() => !isCurrent && onCustomerChange?.(c.id)}
                className="flex items-center gap-2.5 rounded-md px-2 py-1.5 cursor-pointer focus:bg-accent/40"
                style={{
                  background: isCurrent ? 'var(--vf-accent-bg, rgba(99,102,241,0.08))' : undefined,
                }}
              >
                <Avatar label={label} size={22} />
                <div className="flex-1 min-w-0">
                  <p
                    className={`truncate ${isCurrent ? 'font-semibold' : 'font-medium'}`}
                    style={{ fontSize: 12, color: 'var(--vf-text-1)', lineHeight: 1.25 }}
                  >
                    {label}
                  </p>
                  {c.contact_name && (
                    <p
                      className="truncate"
                      style={{ fontSize: 11, color: 'var(--vf-text-3)', marginTop: 1 }}
                    >
                      {c.contact_name}
                    </p>
                  )}
                </div>
                {isCurrent && (
                  <Check
                    size={14}
                    style={{ color: 'var(--vf-accent)', flexShrink: 0 }}
                    strokeWidth={2.5}
                  />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
